import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, OnDestroy, TemplateRef, ViewChild } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { DEVICE_TYPE_OPTIONS, LEVEL_OPTIONS } from '@ids-constants';
import { TargetDeviceService } from '@ids-services';
import { BaseComponent, FormBuilderComponent } from '@microsec/components';
import { SAVE_CHANGES_LABEL } from '@microsec/constants';
import { FormItem } from '@microsec/models';
import { FORM_PARAMS as DEVICE_FORM_PARAMS } from '../../../../../../shared-components/shared-device-form/shared-device-form.component';
import { TagsFieldComponent } from '../../../../../../shared-components/shared-device-form/tags-field/tags-field.component';
import { Subscription } from 'rxjs';
import { SliderModule } from 'primeng/slider';

@Component({
  selector: 'app-bulk-edit-devices-overview-form',
  templateUrl: './bulk-edit-devices-overview-form.component.html',
  styleUrls: ['./bulk-edit-devices-overview-form.component.scss'],
  imports: [CommonModule, ReactiveFormsModule, SliderModule, FormBuilderComponent, TagsFieldComponent],
})
export class BulkEditDevicesOverviewFormComponent extends BaseComponent implements AfterViewInit, OnDestroy {
  fields: FormItem[] = [];

  selectedTags: any[] = [];

  selectedZones: any[] = [];

  manufacturerSubscription: Subscription | null = null;

  modelSubscription: Subscription | null = null;

  @ViewChild('fb') form!: FormBuilderComponent;

  @ViewChild('criticalityField') criticalityField!: TemplateRef<any>;

  @ViewChild('tagsField') tagsField!: TemplateRef<any>;

  @ViewChild('zonesField') zonesField!: TemplateRef<any>;

  DEVICE_FORM_PARAMS = DEVICE_FORM_PARAMS;

  SAVE_CHANGES_LABEL = SAVE_CHANGES_LABEL;

  constructor(private targetDeviceSrv: TargetDeviceService) {
    super();
  }

  async ngAfterViewInit() {
    await this.prepareConfigs();
    this.initForm();
  }

  initForm() {
    const fields: FormItem[] = [
      Object.assign(new FormItem(), {
        name: DEVICE_FORM_PARAMS.MANUFACTURER,
        isCheckboxLabel: true,
        checkboxLabelDefaultValue: false,
        label: 'Manufacturer',
        field: 'autocomplete',
        autoCompleteDropdown: false,
        minLength: 2,
        suggestions: [] as any[],
        onTypeEvent: new EventEmitter(),
        fieldInfo: 'Manufacturer of the device',
        defaultValue: '',
      } as unknown as FormItem),
      Object.assign(new FormItem(), {
        name: DEVICE_FORM_PARAMS.MODEL,
        isCheckboxLabel: true,
        checkboxLabelDefaultValue: false,
        label: 'Model',
        field: 'autocomplete',
        autoCompleteDropdown: false,
        minLength: 2,
        suggestions: [] as any[],
        onTypeEvent: new EventEmitter(),
        onBlurEvent: new EventEmitter(),
        fieldInfo: 'Model of the device',
        defaultValue: '',
      } as FormItem),
      Object.assign(new FormItem(), {
        name: DEVICE_FORM_PARAMS.TYPE,
        isCheckboxLabel: true,
        checkboxLabelDefaultValue: false,
        label: 'Device Type',
        field: 'autocomplete',
        autoCompleteDropdown: true,
        suggestions: this.util.cloneObjectArray(DEVICE_TYPE_OPTIONS).map((type) => type.label),
        fieldInfo: 'Type of the device',
        defaultValue: '',
      } as FormItem),
      Object.assign(new FormItem(), {
        name: DEVICE_FORM_PARAMS.OS,
        isCheckboxLabel: true,
        checkboxLabelDefaultValue: false,
        label: 'Operating System',
        fieldInfo: 'Operating system of the device',
        defaultValue: '',
      } as FormItem),
      Object.assign(new FormItem(), {
        name: DEVICE_FORM_PARAMS.FIRMWARE_VERSION,
        isCheckboxLabel: true,
        checkboxLabelDefaultValue: false,
        label: 'Firmware Version',
        fieldInfo: 'Firmware version of the device',
        defaultValue: '',
      } as FormItem),
      Object.assign(new FormItem(), {
        name: DEVICE_FORM_PARAMS.NETWORK_MAP_LEVEL,
        isCheckboxLabel: true,
        checkboxLabelDefaultValue: false,
        label: 'Network Map Level',
        field: 'dropdown',
        options: this.util.cloneObjectArray(LEVEL_OPTIONS),
        fieldInfo: 'Network map level of the device',
        defaultValue: -1,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: DEVICE_FORM_PARAMS.CRITICALITY,
        isCheckboxLabel: true,
        checkboxLabelDefaultValue: false,
        label: 'Criticality',
        field: 'custom',
        // fieldStyleClass: 'my-auto',
        // rowStyleClass: 'flex align-items-center align-self-center',
        customField: this.criticalityField,
        fieldInfo: 'Criticality of the device',
        defaultValue: 5,
      } as FormItem),
      Object.assign(new FormItem(), {
        name: DEVICE_FORM_PARAMS.TAGS,
        isCheckboxLabel: true,
        checkboxLabelDefaultValue: false,
        label: 'Tag(s)',
        field: 'custom',
        customField: this.tagsField,
        fieldInfo: 'Tags of the device',
        defaultValue: [],
      } as FormItem),
      Object.assign(new FormItem(), {
        name: DEVICE_FORM_PARAMS.ZONES,
        isCheckboxLabel: true,
        checkboxLabelDefaultValue: false,
        label: 'Zone(s)',
        field: 'custom',
        customField: this.zonesField,
        fieldInfo: 'Zones of the device',
        defaultValue: [],
      } as FormItem),
    ];
    this.fields = fields;
    fields.forEach((field) => {
      field.isMediumSize = true;
    });
    this.setupFieldEvents();
  }

  /**
   * Setup field events
   */
  setupFieldEvents() {
    const manufacturerField = this.fields.find((p) => p.name === DEVICE_FORM_PARAMS.MANUFACTURER);
    if (!!manufacturerField) {
      manufacturerField.onTypeEvent?.subscribe(() => {
        this.getManufacturers();
      });
    }
    const modelField = this.fields.find((p) => p.name === DEVICE_FORM_PARAMS.MODEL);
    if (!!modelField) {
      modelField.onTypeEvent?.subscribe(() => {
        this.getModels();
      });
    }
  }

  /**
   * Get manufacturers
   */
  getManufacturers() {
    const manufacturer = (this.form.getControlValue(DEVICE_FORM_PARAMS.MANUFACTURER) as string) || '';
    if (!!manufacturer && manufacturer.length >= (this.fields.find((p) => p.name === DEVICE_FORM_PARAMS.MANUFACTURER)?.minLength || 1)) {
      if (!!this.manufacturerSubscription) {
        this.manufacturerSubscription.unsubscribe();
        this.manufacturerSubscription = null;
      }
      this.manufacturerSubscription = this.targetDeviceSrv.getManufacturers(manufacturer).subscribe({
        next: (result: any[]) => {
          const manufacturers = this.util
            .sortObjectArray(
              this.util.getUniqueObjectArray(
                result.map((p) => ({ ...p, value: ((p.vendor_name as string) || '')?.toLowerCase() })).filter((p) => !!p.vendor_name),
                'vendor_name',
              ),
              'value',
            )
            .map((p) => p.vendor_name);
          const manufacturerField = this.fields.find((p) => p.name === DEVICE_FORM_PARAMS.MANUFACTURER);
          if (!!manufacturerField) {
            manufacturerField.suggestions = manufacturers;
          }
        },
        error: (err: any) => {
          this.showErrorMessage(err);
        },
      });
    }
  }

  /**
   * Get models
   */
  getModels() {
    const manufacturer = (this.form.getControlValue(DEVICE_FORM_PARAMS.MANUFACTURER) as string) || '';
    const model = (this.form.getControlValue(DEVICE_FORM_PARAMS.MODEL) as string) || '';
    if (!!manufacturer && !!model && model.length >= (this.fields.find((p) => p.name === DEVICE_FORM_PARAMS.MODEL)?.minLength || 1)) {
      if (!!this.modelSubscription) {
        this.modelSubscription.unsubscribe();
        this.modelSubscription = null;
      }
      this.modelSubscription = this.targetDeviceSrv.getModels(manufacturer, model).subscribe({
        next: (result: any[]) => {
          const models = this.util
            .sortObjectArray(
              this.util.getUniqueObjectArray(
                result.map((p) => ({ ...p, value: ((p.product_name as string) || '')?.toLowerCase() })).filter((p) => !!p.product_name),
                'product_name',
              ),
              'value',
            )
            .map((p) => p.product_name);
          const modelField = this.fields.find((p) => p.name === DEVICE_FORM_PARAMS.MODEL);
          if (!!modelField) {
            modelField.suggestions = models;
          }
        },
        error: () => {
          const modelField = this.fields.find((p) => p.name === DEVICE_FORM_PARAMS.MODEL);
          if (!!modelField) {
            modelField.suggestions = [];
          }
        },
      });
    }
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    if (!!this.manufacturerSubscription) {
      this.manufacturerSubscription.unsubscribe();
    }
    if (!!this.modelSubscription) {
      this.modelSubscription.unsubscribe();
    }
  }
}
